import {
  isSectionApproved,
  isSectionSettled,
  SECTION_STATUS,
} from '../config/draftReviewConfig'

const flattenSections = (groups = []) =>
  groups.flatMap(({ sections = [] }) => sections)

// "9 / 9 approved" on a group header.
export const countGroupSections = ({ sections = [] } = {}) => ({
  approved: sections.filter(({ status }) => isSectionApproved(status)).length,
  total: sections.length,
})

// The green "All approved" chip only shows for a group that is entirely
// approved — a single excluded or rejected section withholds it.
export const isGroupAllApproved = ({ sections = [] } = {}) =>
  sections.length > 0 &&
  sections.every(({ status }) => isSectionApproved(status))

// The publish queue opens once nothing is left undecided. Excluded and
// rejected sections count as decided; a lingering conflict does not.
export const canSendToPublishQueue = (groups = []) => {
  const sections = flattenSections(groups)
  return (
    sections.length > 0 &&
    sections.every(({ status }) => isSectionSettled(status))
  )
}

/*
 * An edit is a screen-level thing: the reviewer's text is held here and goes
 * nowhere until they approve or reject the section. Approving sends the text
 * with the decision in one write; rejecting throws it away.
 *
 * Whatever was edited, the section comes out PENDING — a conflict someone has
 * picked a side on drops its options and reads like any other section waiting
 * on the approval that still has to happen.
 */
export const toPendingEdit = ({ candidateId = '', value = '' } = {}) => ({
  status: SECTION_STATUS.PENDING,
  ...(candidateId ? { selectedCandidateId: candidateId } : {}),
  value,
})

export const applyPendingEdits = (review, pendingEdits = {}) => {
  if (Object.keys(pendingEdits).length === 0) return review

  return {
    ...review,
    groups: review.groups.map((group) => ({
      ...group,
      sections: group.sections.map((section) =>
        pendingEdits[section.id]
          ? { ...section, ...pendingEdits[section.id] }
          : section,
      ),
    })),
  }
}

// Applies one section's new state in place of the old, leaving the group
// structure untouched. Used after a decision call comes back.
export const replaceSection = (groups = [], sectionId, changes = {}) =>
  groups.map((group) => ({
    ...group,
    sections: group.sections.map((section) =>
      section.id === sectionId ? { ...section, ...changes } : section,
    ),
  }))

// Spaces extracted text out so it does not run together: a line break opens a
// blank line, and a `;` — or a `: ` — starts a new line. A colon with no space
// after it is left alone, so times and URLs stay intact.
//
// The edit box opens on this too, so saved text comes back through it. A break
// right after `;` or `:` is therefore left single, or every save would turn
// those lines into paragraphs.
export const spaceLineBreaks = (value = '') =>
  value
    .replace(/(?<![;:])\n+/g, '\n\n')
    .replace(/;[ \t]*(?=\S)/g, ';\n')
    .replace(/:[ \t]+(?=\S)/g, ':\n')
    .trim()

export const findSection = (groups = [], sectionId) =>
  flattenSections(groups).find(({ id }) => id === sectionId)

// The value a section currently stands behind: its own `content` once one has
// been chosen or typed, otherwise the candidate the reviewer picked, falling
// back to the only candidate there is.
// Resolves the current content including a pending edit or chosen candidate.
export const getSectionValue = ({
  candidates = [],
  selectedCandidateId = '',
  value = '',
} = {}) => {
  if (value) return value
  const selected = candidates.find(({ id }) => id === selectedCandidateId)
  if (selected) return selected.value
  return candidates.length === 1 ? candidates[0].value : ''
}
